// src/components/meetstandaard/EffectDatabaseItemsList.jsx

import { useState } from "react";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { getSectorInfo } from "../../helpers/meetstandaard/sectorCatalog";

const TARGETGROUP_LABELS = {
  volwassenen: "Volwassenen",
  "kinderen-vanaf-groep-5": "Kinderen (vanaf groep 5)",
  "jongeren-vanaf-middelbare-school": "Jongeren (vanaf middelbare school)",
};

const EffectdatabaseItemsList = ({
  setSelectedEffects,
  category,
  selectedSector = "all",
  selectedEffects = [],
  scrollFocusId = "",
}) => {
  const [expandedCategoryList, setExpandedCategoryList] = useState("");

  const effectsFiltered = (category.effects || []).filter((effect) => {
    if (!selectedSector || selectedSector === "all") return true;
    const sectors = Array.isArray(effect.sectors) ? effect.sectors : [];
    return sectors.includes(selectedSector);
  });

  const isSelected = (effectId) =>
    selectedEffects.some((x) => String(x.id) === String(effectId));

  const getSelected = (effectId) =>
    selectedEffects.find((x) => String(x.id) === String(effectId));

  // Detect which targetgroups exist for an effect (only if questions have custom targetgroups)
  const getEffectTargetGroupOptions = (effect) => {
    const set = new Set();

    (effect.questions || []).forEach((q) => {
      if (q && q.targetGroupsMode === "custom" && Array.isArray(q.targetGroups)) {
        q.targetGroups.forEach((tg) => set.add(tg));
      }
    });

    return Array.from(set);
  };

  // Does a question apply to a selected targetgroup?
  const questionAppliesToTargetGroup = (question, selectedTargetGroup) => {
    const mode = question?.targetGroupsMode || "all";
    if (mode === "all") return true;

    const groups = Array.isArray(question?.targetGroups) ? question.targetGroups : [];
    if (!selectedTargetGroup) return false;

    return groups.includes(selectedTargetGroup);
  };

  // Filter questions for display (only when an effect is selected AND a targetgroup is chosen)
  const getDisplayQuestions = (effect, selectedTargetGroup) => {
    const questions = Array.isArray(effect.questions) ? effect.questions : [];

    // If no targetgroup chosen, show everything (prevents “empty” confusion)
    if (!selectedTargetGroup) return questions;

    // Show questions that apply to this targetgroup (includes "all" ones)
    return questions.filter((q) => questionAppliesToTargetGroup(q, selectedTargetGroup));
  };

  const effectHandler = (e, effect) => {
    const checked = e.target.checked;
    const categoryName = e.target.dataset.category;

    const targetGroupOptions = getEffectTargetGroupOptions(effect);
    const needsTargetGroupChoice = targetGroupOptions.length > 0;

    if (checked) {
      setSelectedEffects((prev) => {
        const already = prev.find((x) => String(x.id) === String(effect.id));
        if (already) return prev;

        // auto select if only one option exists
        const autoTargetGroup =
          needsTargetGroupChoice && targetGroupOptions.length === 1
            ? targetGroupOptions[0]
            : "";

        return [
          ...prev,
          {
            effect: effect.name,
            description: effect.description || "",
            category: categoryName,
            questions: effect.questions,
            id: effect.id,
            sectors: Array.isArray(effect.sectors) ? effect.sectors : [],
            targetGroup: autoTargetGroup,
            targetGroupRequired: needsTargetGroupChoice,
            // Provenance of the questions being copied. Absent on legacy
            // /database effects, which carry no version — an empty stamp
            // records "unversioned", which is the truth for those.
            slug: effect.slug || "",
            standaard: effect.standaard || "",
            standaardVersion: effect.standaardVersion || "",
          },
        ];
      });
    } else {
      setSelectedEffects((prev) =>
        prev.filter((item) => String(item.id) !== String(effect.id))
      );
    }
  };

  const updateSelectedEffectTargetGroup = (effectId, targetGroup) => {
    setSelectedEffects((prev) =>
      prev.map((item) =>
        String(item.id) === String(effectId) ? { ...item, targetGroup } : item
      )
    );
  };

  const isTargetGroupMissing = (effectId) => {
    const sel = getSelected(effectId);
    if (!sel) return false;
    if (!sel.targetGroupRequired) return false;
    return !sel.targetGroup;
  };

  return (
    <div className="effect-database-category">
      <div
        className={`effect-selector-title-container ${expandedCategoryList ? "expanded" : ""}`}
        onClick={() => setExpandedCategoryList(!expandedCategoryList)}
      >
        <h3>{category.name}</h3>
        <KeyboardArrowDownOutlinedIcon
          className={`effect-category-arrow ${expandedCategoryList ? "open" : ""}`}
        />
      </div>

      {expandedCategoryList && (
        <div className="effect-category-table-wrapper">
          <table className="effect-category-table">
            <thead>
              <tr>
                <th></th>
                <th>EFFECT</th>
                <th>VRAGEN</th>
                <th>OMSCHRIJVING</th>
              </tr>
            </thead>

            <tbody>
              {effectsFiltered.map((effect) => {
                const selected = getSelected(effect.id);
                const checked = isSelected(effect.id);

                const targetGroupOptions = getEffectTargetGroupOptions(effect);
                const needsTargetGroupChoice = targetGroupOptions.length > 0;

                const missing = isTargetGroupMissing(effect.id);
                const pulse = String(scrollFocusId) === String(effect.id);

                // ✅ Filter questions shown when a targetgroup is selected
                const selectedTargetGroup = selected?.targetGroup || "";
                const questionsToShow = checked
                  ? getDisplayQuestions(effect, selectedTargetGroup)
                  : (Array.isArray(effect.questions) ? effect.questions : []);

                return (
                  <tr
                    key={effect.id || effect.name}
                    id={`msi-effect-row-${effect.id}`}
                    className={[
                      missing ? "effect-row-missing-targetgroup" : "",
                      pulse ? "effect-row-pulse" : "",
                    ].join(" ")}
                  >
                    <td>
                      <input
                        type="checkbox"
                        data-category={category.name}
                        data-id={effect.id}
                        checked={checked}
                        onChange={(e) => effectHandler(e, effect)}
                      />
                    </td>

                    <td>
                      <div className="effect-name-with-sectors">
                        <p className="effect-name">{effect.name}</p>

                        {Array.isArray(effect.sectors) && effect.sectors.length > 0 && (
                          <div className="effect-sectors">
                            {effect.sectors.map((sector) => (
                              <span key={sector} className="effect-sector-pill">
                                {getSectorInfo(sector).label}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Targetgroup chooser */}
                        {checked && needsTargetGroupChoice && (
                          <div className="effect-targetgroup-block">
                            <div className="effect-targetgroup-title">
                              <span>Doelgroep kiezen</span>
                              {missing ? (
                                <span className="effect-targetgroup-required">verplicht</span>
                              ) : null}
                            </div>

                            <div className="effect-targetgroup-options">
                              {targetGroupOptions.map((tg) => (
                                <label key={tg} className="effect-targetgroup-option">
                                  <input
                                    type="radio"
                                    name={`tg-${effect.id}`}
                                    value={tg}
                                    checked={selected?.targetGroup === tg}
                                    onChange={() => updateSelectedEffectTargetGroup(effect.id, tg)}
                                  />
                                  <span>{TARGETGROUP_LABELS[tg] || tg}</span>
                                </label>
                              ))}
                            </div>

                            {missing ? (
                              <div className="effect-targetgroup-hint">
                                Dit effect bevat vragen die verschillen per doelgroep. Kies er één om door te gaan.
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* ✅ QUESTIONS column now shows filtered list */}
                    <td>
                      {questionsToShow && questionsToShow.length > 0 ? (
                        questionsToShow.map((question, idx) => (
                          <div key={question.id || idx} className="effect-question-row">
                            <p>{idx + 1}.</p>
                            <p>{question.name}</p>
                          </div>
                        ))
                      ) : (
                        <p className="effect-empty-questions">
                          Geen vragen voor deze doelgroep.
                        </p>
                      )}
                    </td>

                    <td>
                      <p className="effect-description">{effect.description}</p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {effectsFiltered.length === 0 && (
            <p className="effect-empty-hint">
              Geen effecten in deze categorie voor deze sector.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EffectdatabaseItemsList;
